import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Send, Shield, CheckCircle, Clock, AlertCircle, Users, Mail, Sparkles, Eye, Filter, Copy, Edit2, Lock } from 'lucide-react';

const ServiceAgreementsGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Service Agreements Guide</h1>
          <p className="text-lg text-gray-600">
            Send legally binding digital agreements to customers before starting work
          </p>
        </div>

        {/* What Are Service Agreements */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">What Are Service Agreements?</h2>
              <p className="text-gray-700 mb-3">
                Service agreements are digital documents that customers sign before you start work. They protect both you 
                and your customer by clearly defining the scope of work, terms, payment expectations, and policies.
              </p>
              <p className="text-gray-700">
                SuddyFlow's electronic signatures are legally binding and include timestamps, IP addresses, and audit trails - 
                just like paper contracts, but faster and easier to manage.
              </p>
            </div>
          </div>
        </div>

        {/* Why Use Service Agreements */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Why Use Service Agreements?</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Protect Your Business</p>
                <p className="text-sm text-gray-700">Clear terms prevent disputes and misunderstandings</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Look Professional</p>
                <p className="text-sm text-gray-700">Shows you run a legitimate, organized business</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Set Expectations</p>
                <p className="text-sm text-gray-700">Customers know exactly what to expect from the service</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Legal Protection</p>
                <p className="text-sm text-gray-700">Electronic signatures are legally binding in all 50 states</p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">How Service Agreements Work</h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Step 1 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">You Send Agreement to Customer</h3>
                <p className="text-gray-700 mb-2">
                  From the Agreements page, click "Send Agreement" → Select customer, template, and optionally link a job. 
                  Customer receives an email with a secure signing link.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    <strong>Pro Tip:</strong> Send agreements as soon as a customer books a job. Get it signed before you show up!
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Customer Reviews & Signs</h3>
                <p className="text-gray-700 mb-2">
                  Customer clicks the link, reviews the agreement terms, provides their signature (drawn or typed), 
                  and clicks "Sign Agreement". No login required - super easy for customers!
                </p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <strong className="text-gray-900">Customer sees:</strong>
                  <ul className="mt-1 space-y-1 text-gray-600 ml-4">
                    <li>• Agreement title and content</li>
                    <li>• Any optional consents (photo, cancellation, weather)</li>
                    <li>• Digital signature pad</li>
                    <li>• Security notice (encrypted, IP logged)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                ✓
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Done! Agreement Signed</h3>
                <p className="text-gray-700 mb-2">
                  Both you and the customer receive confirmation emails. You can view the signed agreement, signature details 
                  (timestamp, IP address, etc.) anytime from the Agreements page.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-900">
                    <strong>Now you're protected:</strong> You have a legally binding agreement before starting work!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Notifications */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="text-blue-600" size={24} />
              Email Notifications
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              SuddyFlow automatically sends emails at key moments in the agreement process:
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Send size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Agreement Sent</p>
                  <p className="text-sm text-gray-600">Customer receives email with "Review & Sign Agreement" button and expiration date</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock size={16} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Reminder Sent (Resend)</p>
                  <p className="text-sm text-gray-600">When you click "Resend", customer gets a reminder email with fresh signing link</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Signed Confirmation</p>
                  <p className="text-sm text-gray-600">Customer receives confirmation email with agreement details, signature info, and reference ID for their records</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sending Agreements */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Send className="text-blue-600" size={24} />
              How to Send an Agreement
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Step-by-Step:</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">1.</span>
                  <p className="text-sm text-gray-700">
                    Navigate to <strong>Agreements</strong> from the sidebar menu
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">2.</span>
                  <p className="text-sm text-gray-700">
                    Click <strong>"Send Agreement"</strong> button (top-right)
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">3.</span>
                  <p className="text-sm text-gray-700">
                    <strong>Select Customer:</strong> Choose from your customer list (must have email address)
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">4.</span>
                  <p className="text-sm text-gray-700">
                    <strong>Choose Template:</strong> Pick which agreement to send (General Service, Pressure Washing, etc.)
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">5.</span>
                  <p className="text-sm text-gray-700">
                    <strong>Link Job (Optional):</strong> If this agreement is for a specific job, select it. This auto-fills job details in the agreement.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">6.</span>
                  <p className="text-sm text-gray-700">
                    Click <strong>"Send Agreement"</strong> - Done! Customer receives email instantly.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-green-900 font-medium mb-1">After Sending:</p>
                  <p className="text-sm text-green-800">
                    You'll see a success screen with the signing link. You can copy this link to share via text message 
                    if the customer prefers that over email.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-900 font-medium mb-1">Important:</p>
                  <p className="text-sm text-amber-800">
                    Customers MUST have an email address to receive agreements. If a customer doesn't have email, 
                    add it to their profile first or share the signing link manually (click "Copy Link" after sending).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Agreement Templates */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Agreement Templates</h2>
          </div>
          <div className="p-6 space-y-5">
            <p className="text-gray-700">
              SuddyFlow provides professionally written agreement templates. Access templates from the 
              <strong> Templates tab</strong> on the Agreements page.
            </p>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Two Types of Templates:</h3>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={18} className="text-blue-600" />
                    <p className="font-medium text-gray-900">System Templates</p>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Pre-built templates provided by SuddyFlow. Everyone can use these as-is. 
                    Pro and Enterprise users can click "Customize" to create their own version.
                  </p>
                  <ul className="text-sm text-gray-600 ml-4 space-y-1">
                    <li>• General Service Agreement</li>
                    <li>• Pressure Washing Agreement</li>
                    <li>• Industry-specific templates</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={18} className="text-purple-600" />
                    <p className="font-medium text-gray-900">My Templates (Custom)</p>
                    <span className="px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full">Pro+</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Your customized versions of templates. Edit the title, content, and consent options 
                    to match your business needs.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Template Features:</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Preview Before Sending</p>
                    <p className="text-sm text-gray-600">Click "Preview" to see how the agreement looks with sample data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Merge Fields</p>
                    <p className="text-sm text-gray-600">Templates automatically fill in customer name, job details, your business info</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Optional Consents</p>
                    <p className="text-sm text-gray-600">Photo/video consent, cancellation policy, weather policy checkboxes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Access Levels */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Lock size={20} className="text-purple-600" />
            Template Access by Plan
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-purple-200">
                  <th className="text-left py-2 pr-4 font-semibold text-gray-900">Feature</th>
                  <th className="text-center py-2 px-4 font-semibold text-gray-900">Solo</th>
                  <th className="text-center py-2 px-4 font-semibold text-gray-900">Pro</th>
                  <th className="text-center py-2 px-4 font-semibold text-gray-900">Enterprise</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b border-purple-100">
                  <td className="py-2 pr-4">Use system templates</td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-purple-100">
                  <td className="py-2 pr-4">Preview templates</td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-purple-100">
                  <td className="py-2 pr-4">Copy & customize templates</td>
                  <td className="text-center py-2 px-4"><span className="text-gray-400">—</span></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-purple-100">
                  <td className="py-2 pr-4">Edit custom templates</td>
                  <td className="text-center py-2 px-4"><span className="text-gray-400">—</span></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">Create templates from scratch</td>
                  <td className="text-center py-2 px-4"><span className="text-gray-400">—</span></td>
                  <td className="text-center py-2 px-4"><span className="text-gray-400">—</span></td>
                  <td className="text-center py-2 px-4"><CheckCircle size={16} className="text-green-600 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-purple-700 mt-4">
            <strong>Trial users</strong> get full Enterprise access to test all features before subscribing.
          </p>
        </div>

        {/* Customizing Templates */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Edit2 className="text-purple-600" size={24} />
              Customizing Templates
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Pro+</span>
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-gray-700">
              Pro and Enterprise users can customize templates to match their business:
            </p>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How to Customize:</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">1.</span>
                  <p className="text-sm text-gray-700">
                    Go to <strong>Agreements → Templates</strong> tab
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">2.</span>
                  <p className="text-sm text-gray-700">
                    Find a system template and click <strong>"Customize"</strong>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">3.</span>
                  <p className="text-sm text-gray-700">
                    This creates a copy in "My Templates" and opens the editor
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">4.</span>
                  <p className="text-sm text-gray-700">
                    Edit the template name, title, content, and consent options
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 mt-0.5">5.</span>
                  <p className="text-sm text-gray-700">
                    Click <strong>"Save Changes"</strong> - your custom template is ready to use!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Available Merge Fields:</strong> Use these in your template content and they'll be 
                automatically replaced with actual data when sent:
              </p>
              <p className="text-xs text-blue-800 mt-2 font-mono bg-blue-100 p-2 rounded">
                {`{{CustomerName}}, {{CustomerFirstName}}, {{BusinessName}}, {{BusinessPhone}}, {{BusinessEmail}}, {{JobDescription}}, {{JobAddress}}, {{JobDate}}, {{JobTime}}, {{JobTotal}}`}
              </p>
            </div>
          </div>
        </div>

        {/* Agreement Statuses */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Understanding Agreement Statuses</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full flex-shrink-0">
                <Clock size={14} />
                Pending
              </span>
              <div>
                <p className="font-medium text-gray-900">Waiting for Customer Signature</p>
                <p className="text-sm text-gray-600">
                  Agreement sent but not signed yet. Link expires in 7 days (default). You can resend or view how many times they've opened it.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full flex-shrink-0">
                <CheckCircle size={14} />
                Signed
              </span>
              <div>
                <p className="font-medium text-gray-900">Customer Signed Successfully</p>
                <p className="text-sm text-gray-600">
                  Agreement is legally binding and complete. Customer received confirmation email. You can view signature details, timestamp, and IP address anytime.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full flex-shrink-0">
                <AlertCircle size={14} />
                Expired
              </span>
              <div>
                <p className="font-medium text-gray-900">Signing Link Expired</p>
                <p className="text-sm text-gray-600">
                  Customer didn't sign within the expiration period. Click "Resend" to generate a new link with fresh expiration.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full flex-shrink-0">
                <AlertCircle size={14} />
                Voided
              </span>
              <div>
                <p className="font-medium text-gray-900">Agreement Cancelled</p>
                <p className="text-sm text-gray-600">
                  You manually voided this agreement. It's no longer valid. Used when you cancel a job or need to send a corrected agreement.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Managing Agreements */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Managing Agreements</h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Eye size={18} className="text-blue-600" />
                View Agreement Details
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Click the eye icon next to any agreement to see full details: customer info, signature details, 
                timestamp, IP address, view count, and consent selections.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Send size={18} className="text-green-600" />
                Resend Agreement
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                If a customer didn't sign or the link expired, click "Resend" to generate a new signing link. 
                They'll get a reminder email with a new 7-day expiration.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Copy size={18} className="text-purple-600" />
                Copy Signing Link
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                After sending, you can copy the signing link to share via text message or any other method. 
                Useful for customers who prefer texts over email.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <AlertCircle size={18} className="text-red-600" />
                Void Agreement
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Need to cancel an agreement? Click "Void" and add an optional reason. This marks it as cancelled 
                and prevents customers from signing if they still have the link.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <strong className="text-amber-900">Use cases:</strong> Job cancelled, customer changed mind, 
                need to send corrected terms, or sent to wrong customer.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Filter size={18} className="text-gray-600" />
                Filter & Search
              </h3>
              <p className="text-sm text-gray-700">
                Use the status filters (Pending, Signed, Expired, Voided) to quickly find specific agreements. 
                Or search by customer name/email to find all agreements for a customer.
              </p>
            </div>
          </div>
        </div>

        {/* Electronic Signature Legal */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Are Electronic Signatures Legal?</h2>
              <p className="text-gray-700 mb-3">
                <strong>Yes!</strong> Electronic signatures have been legally binding in the United States since 2000 
                under the ESIGN Act and UETA (Uniform Electronic Transactions Act). They carry the same legal weight 
                as handwritten signatures on paper.
              </p>
              
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">What SuddyFlow Captures to Ensure Validity:</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-gray-700">Customer's digital signature (drawn or typed)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-gray-700">Full legal name</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-gray-700">Timestamp (exact date/time signed)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-gray-700">IP address (proves who signed)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-gray-700">Consent to electronic signature</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-gray-700">Complete audit trail</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mt-4">
                <strong>Note:</strong> We're not lawyers, so consult with legal counsel if you have specific questions 
                about your agreements. But electronic signatures through SuddyFlow are designed to meet all legal requirements.
              </p>
            </div>
          </div>
        </div>

        {/* Common Questions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Common Questions</h2>
          
          <div className="space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What if a customer doesn't have email?
              </h3>
              <p className="text-sm text-gray-700">
                After sending the agreement, you'll see a signing link you can copy. Share it via text message, 
                or have them sign in person using your phone/tablet. The link works on any device without requiring login.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can customers sign on their phone?
              </h3>
              <p className="text-sm text-gray-700">
                Yes! The signing page is fully mobile-friendly. Customers can easily sign with their finger on a phone or tablet. 
                They can also type their name instead of drawing a signature.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                How long does the signing link last?
              </h3>
              <p className="text-sm text-gray-700">
                By default, 7 days. After expiration, the link won't work - but you can easily click "Resend" to generate a new link.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I send multiple agreements to the same customer?
              </h3>
              <p className="text-sm text-gray-700">
                Absolutely! You might send a general service agreement, then a specific job-based agreement later, or send 
                new agreements for each recurring service. There's no limit.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What happens if I accidentally send to the wrong customer?
              </h3>
              <p className="text-sm text-gray-700">
                Immediately click "Void" on that agreement. This invalidates the signing link. Then send a new agreement 
                to the correct customer. Always double-check before clicking "Send"!
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Does the customer get a copy of the signed agreement?
              </h3>
              <p className="text-sm text-gray-700">
                Yes! As soon as they sign, they receive a confirmation email with the agreement details, who signed, 
                when it was signed, and a reference ID for their records.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Do I need agreements for every job?
              </h3>
              <p className="text-sm text-gray-700">
                It's smart practice, but not always required. Use agreements for:
              </p>
              <ul className="mt-2 space-y-1 ml-6 text-sm text-gray-600">
                <li>• New customers</li>
                <li>• Large or complex jobs</li>
                <li>• Jobs with potential liability (heights, chemicals, expensive property)</li>
                <li>• Customers who seem difficult or litigious</li>
              </ul>
              <p className="text-sm text-gray-700 mt-2">
                For small routine work with established customers, you might skip it - but having one doesn't hurt!
              </p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💡 Best Practices</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Send Agreements Early</h3>
              <p className="text-sm text-amber-800">
                Send agreements immediately after booking a job - don't wait until you're at their door. This gives customers 
                time to review and sign before you arrive. No awkward "sign this first" moment.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Follow Up on Pending Agreements</h3>
              <p className="text-sm text-amber-800">
                Check your Agreements page daily for pending items. If a customer hasn't signed after 2-3 days, 
                send a friendly reminder text: "Just making sure you got the service agreement I sent. Need me to resend it?"
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Don't Start Work Without Signature</h3>
              <p className="text-sm text-amber-800">
                Make it a policy: no signed agreement = no work. This protects you legally and sets professional boundaries. 
                If someone refuses to sign, that's a red flag - walk away.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Keep Templates Simple</h3>
              <p className="text-sm text-amber-800">
                Don't write a 10-page legal document. Keep agreements clear, concise, and easy to understand. 
                Complicated agreements scare customers away. Cover the essentials: scope, payment terms, liability, cancellation.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Use the Templates Tab</h3>
              <p className="text-sm text-amber-800">
                Take a few minutes to preview the available templates and customize one if you're on Pro or Enterprise. 
                A template that reflects your business voice builds trust with customers.
              </p>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need Help with Service Agreements?</h3>
          <p className="text-gray-700 mb-4">
            Questions about templates, legal validity, or how to use agreements effectively?
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Mail size={18} />
            Email Support
          </a>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceAgreementsGuide;